import React, { useEffect, useMemo, useRef, useState } from 'react';
import LeadCaptureModal from './LeadCaptureModal';
import { postLead, splitName, trackEvent } from '../lib/leads';
import { trackEvent as metaTrack } from '../lib/metaTrack';

const STATUS_COLORS = {
  disponible:     '#2e9d4f',
  apartado:       '#f4c430',
  vendido:        '#f6802c',
  segunda_etapa:  '#9aa4ad',
};
const STATUS_LABELS = {
  disponible:     'Disponible',
  apartado:       'Apartado',
  vendido:        'Vendido',
  segunda_etapa:  '2da etapa',
};
const ROMAN = ['', 'I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII','XIII','XIV','XV','XVI','XVII','XVIII','XIX','XX'];
const toRoman = (n) => ROMAN[n] || String(n);

export default function LotMapInline() {
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [mzFilter, setMzFilter] = useState('all');
  const [hover, setHover] = useState(null);
  const [selected, setSelected] = useState(null);
  const [imgSize, setImgSize] = useState({ w: 2400, h: 1280 });
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [leadModal, setLeadModal] = useState(null); // {lote} or null
  const planoRef = useRef(null);

  useEffect(() => {
    // Try API first, fallback to static JSON
    const API_BASE = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_BASE}/api/lots`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => {
        const lots = Array.isArray(data?.lots) ? data.lots : [];
        if (lots.length > 0) {
          setLotes(lots);
          setLoading(false);
        } else {
          throw new Error('empty');
        }
      })
      .catch(() => {
        // Fallback: static JSON
        fetch('/data/lotes.json')
          .then(r => r.json())
          .then(data => { setLotes(Array.isArray(data) ? data : []); setLoading(false); })
          .catch(() => { setLotes([]); setLoading(false); });
      });
  }, []);

  // Cierra drawer con ESC
  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === 'Escape') setSelected(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const counts = useMemo(() => ({
    disponible:    lotes.filter(l => l.estatus === 'disponible').length,
    apartado:      lotes.filter(l => l.estatus === 'apartado').length,
    vendido:       lotes.filter(l => l.estatus === 'vendido').length,
    segunda_etapa: lotes.filter(l => l.estatus === 'segunda_etapa').length,
    total:         lotes.length,
  }), [lotes]);

  const manzanas = useMemo(() => {
    const set = new Set(lotes.map(l => l.manzana));
    return Array.from(set).sort((a, b) => a - b);
  }, [lotes]);

  const visibleLotes = useMemo(() => {
    return lotes.filter(l => {
      if (filter !== 'all' && l.estatus !== filter) return false;
      if (mzFilter !== 'all' && l.manzana !== mzFilter) return false;
      return true;
    });
  }, [lotes, filter, mzFilter]);

  const hoverLote = lotes.find(l => l.id === hover);
  const selectedLote = lotes.find(l => l.id === selected);

  const handlePinEnter = (e, lote) => {
    setHover(lote.id);
    const rect = planoRef.current?.getBoundingClientRect();
    if (rect) {
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  const handlePinMove = (e) => {
    const rect = planoRef.current?.getBoundingClientRect();
    if (rect) {
      setTooltipPos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
  };

  return (
    <section id="disponibilidad" className="lmi-section v4" data-testid="lotmap-inline">
      <div className="page-padding">
        <div className="container-large">
          {/* HEAD */}
          <div className="lmi-head">
            <div className="text-label" style={{ color: 'var(--brand-accent)' }}>masterplan interactivo</div>
            <h2 className="sm-h2 font-color-1" data-reveal>
              Disponibilidad de <span className="serif-accent">lotes en vivo</span>
            </h2>
            <p className="text-size-medium" data-reveal style={{ maxWidth: '720px', color: 'var(--body-font)' }}>
              Explora el plano y haz click sobre los lotes en verde para conocer modelo, ubicación y solicitar información directa.
            </p>
          </div>

          {/* FILTER BAR — navy unified, includes manzana selector */}
          <div className="lmi-toolbar" data-testid="lmi-toolbar">
            <div className="lmi-filter-pills" role="tablist" aria-label="Filtrar por estatus">
              <button
                type="button"
                className={`lmi-fp${filter === 'all' ? ' active' : ''}`}
                onClick={() => setFilter('all')}
                data-testid="fp-all"
                aria-label={`Todos · ${counts.total}`}
              >
                <i className="lmi-fp-mix" />
                <span>Todos</span>
                <span className="lmi-fp-count">{counts.total}</span>
              </button>
              <span className="lmi-fp-sep" aria-hidden="true" />
              <button
                type="button"
                className={`lmi-fp${filter === 'disponible' ? ' active' : ''}`}
                onClick={() => setFilter('disponible')}
                data-testid="fp-disponible"
                aria-label={`Disponibles · ${counts.disponible}`}
              >
                <i style={{ background: STATUS_COLORS.disponible }} />
                <span>Disponibles</span>
                <span className="lmi-fp-count">{counts.disponible}</span>
              </button>
              <span className="lmi-fp-sep" aria-hidden="true" />
              <button
                type="button"
                className={`lmi-fp${filter === 'apartado' ? ' active' : ''}`}
                onClick={() => setFilter('apartado')}
                data-testid="fp-apartado"
                aria-label={`Apartados · ${counts.apartado}`}
              >
                <i style={{ background: STATUS_COLORS.apartado }} />
                <span>Apartados</span>
                <span className="lmi-fp-count">{counts.apartado}</span>
              </button>
              <span className="lmi-fp-sep" aria-hidden="true" />
              <button
                type="button"
                className={`lmi-fp${filter === 'vendido' ? ' active' : ''}`}
                onClick={() => setFilter('vendido')}
                data-testid="fp-vendido"
                aria-label={`Vendidos · ${counts.vendido}`}
              >
                <i style={{ background: STATUS_COLORS.vendido }} />
                <span>Vendidos</span>
                <span className="lmi-fp-count">{counts.vendido}</span>
              </button>
              <span className="lmi-fp-sep" aria-hidden="true" />
              <button
                type="button"
                className={`lmi-fp${filter === 'segunda_etapa' ? ' active' : ''}`}
                onClick={() => setFilter('segunda_etapa')}
                data-testid="fp-segunda"
                aria-label={`2da etapa · ${counts.segunda_etapa}`}
              >
                <i style={{ background: STATUS_COLORS.segunda_etapa }} />
                <span>2da etapa</span>
                <span className="lmi-fp-count">{counts.segunda_etapa}</span>
              </button>
              <span className="lmi-fp-sep" aria-hidden="true" />
              <label className="lmi-fp lmi-fp-select-wrap" data-testid="lmi-mz-wrap">
                <span className="icon-font lmi-fp-mz-icon">grid_view</span>
                <select
                  className="lmi-fp-select"
                  value={mzFilter}
                  onChange={(e) => setMzFilter(e.target.value === 'all' ? 'all' : parseInt(e.target.value, 10))}
                  data-testid="lmi-mz"
                >
                  <option value="all">Todas las manzanas</option>
                  {manzanas.map(m => (
                    <option key={m} value={m}>Manzana {toRoman(m)}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/* PLANO FULL WIDTH */}
          <div className="lmi-plano-full" ref={planoRef} data-testid="lmi-plano">
            {loading && <div className="lmi-loading">Cargando lotes…</div>}
            <div
              className={['lmi-canvas-fit', (hover || selected) ? 'is-focused' : ''].join(' ')}
              style={{
                aspectRatio: `${imgSize.w} / ${imgSize.h}`,
                '--focus-x': `${(hoverLote || selectedLote)?.x ?? 50}%`,
                '--focus-y': `${(hoverLote || selectedLote)?.y ?? 50}%`,
              }}
            >
              <img
                src="/images/masterplan-cad.webp"
                alt="Plano CAD Santa Mónica"
                className="lmi-plano-img"
                draggable={false}
                onLoad={(e) => setImgSize({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
              />
              {visibleLotes.map(lote => {
                const isHover = hover === lote.id;
                const isSelected = selected === lote.id;
                const interactive = lote.estatus === 'disponible' || lote.estatus === 'apartado';
                const color = STATUS_COLORS[lote.estatus] || '#f6802c';
                return (
                  <button
                    type="button"
                    key={lote.id}
                    className={[
                      'lmi-pin',
                      `s-${lote.estatus}`,
                      isHover && 'hover',
                      isSelected && 'selected',
                      interactive && 'interactive',
                    ].filter(Boolean).join(' ')}
                    style={{
                      left: `${lote.x}%`,
                      top: `${lote.y}%`,
                      background: color,
                    }}
                    onMouseEnter={(e) => handlePinEnter(e, lote)}
                    onMouseMove={handlePinMove}
                    onMouseLeave={() => { if (selected !== lote.id) setHover(null); }}
                    onFocus={(e) => handlePinEnter(e, lote)}
                    onBlur={() => { if (selected !== lote.id) setHover(null); }}
                    onClick={(e) => {
                      if (!interactive) return;
                      // Pin a sticky tooltip at this position (works on touch as the "first tap")
                      const rect = planoRef.current?.getBoundingClientRect();
                      if (rect) {
                        const cx = e.clientX - rect.left;
                        const cy = e.clientY - rect.top;
                        setTooltipPos({ x: cx, y: cy });
                      }
                      setHover(lote.id);
                      setSelected(lote.id);
                      // Meta Pixel ViewContent on lot selection
                      metaTrack('ViewContent', {
                        content_name: `Lote ${lote.numero} Manzana ${lote.manzana} · ${lote.modelo}`,
                        content_category: 'lote',
                        content_ids: [lote.id],
                        content_type: 'product',
                      });
                    }}
                    disabled={!interactive}
                    aria-label={`Lote ${lote.numero} Manzana ${toRoman(lote.manzana)} — ${STATUS_LABELS[lote.estatus]}`}
                    data-testid={`lmi-pin-${lote.id}`}
                  >
                    {interactive && (isHover || isSelected) && <span className="lmi-pin-ring" />}
                    <span className="lmi-pin-num">{lote.numero}</span>
                  </button>
                );
              })}

              {/* SMART TOOLTIP — enriched, includes CTA, works on hover AND on tap (sticky) */}
              {(hoverLote || selectedLote) && (
                <div
                  className={`lmi-tooltip lmi-tooltip-rich s-${(selectedLote || hoverLote).estatus}${selectedLote ? ' is-sticky' : ''}`}
                  style={{
                    left: `${tooltipPos.x}px`,
                    top: `${tooltipPos.y}px`,
                  }}
                  data-testid="lmi-tooltip"
                  onClick={(e) => e.stopPropagation()}
                >
                  {(() => {
                    const lote = selectedLote || hoverLote;
                    const status = lote.estatus;
                    return (
                      <>
                        {selectedLote && (
                          <button
                            type="button"
                            className="lmi-tt-close"
                            onClick={() => { setSelected(null); setHover(null); }}
                            aria-label="Cerrar"
                            data-testid="lmi-tt-close"
                          >
                            <span className="icon-font">close</span>
                          </button>
                        )}
                        <div className="lmi-tt-tag" style={{ background: STATUS_COLORS[status] }}>
                          {STATUS_LABELS[status]}
                        </div>
                        <div className="lmi-tt-title">
                          Lote <strong>{lote.numero}</strong> · Mz <strong>{toRoman(lote.manzana)}</strong>
                        </div>
                        <div className="lmi-tt-grid">
                          <div className="lmi-tt-cell">
                            <span className="lmi-tt-label">Modelo</span>
                            <span className="lmi-tt-val">{lote.modelo}</span>
                          </div>
                          <div className="lmi-tt-cell">
                            <span className="lmi-tt-label">Terreno</span>
                            <span className="lmi-tt-val">{lote.terreno} m²</span>
                          </div>
                          <div className="lmi-tt-cell">
                            <span className="lmi-tt-label">Construcción</span>
                            <span className="lmi-tt-val">{lote.construccion} m²</span>
                          </div>
                        </div>

                        {/* Sticky-only: CTA buttons */}
                        {selectedLote && status === 'disponible' && (
                          <button
                            type="button"
                            className="lmi-tt-cta"
                            data-testid="lmi-tt-cta"
                            onClick={() => {
                              setLeadModal({ lote, mode: 'reservar' });
                            }}
                          >
                            <span className="icon-font">mark_email_read</span>
                            Solicitar información
                            <span className="icon-font">arrow_forward</span>
                          </button>
                        )}
                        {selectedLote && status === 'apartado' && (
                          <>
                            <p className="lmi-tt-msg">Este lote ya está apartado.</p>
                            <button
                              type="button"
                              className="lmi-tt-cta lmi-tt-cta-secondary"
                              data-testid="lmi-tt-cta-similar"
                              onClick={() => {
                                setLeadModal({ lote, mode: 'similar' });
                              }}
                            >
                              <span className="icon-font">search</span>
                              Pregunta por uno similar
                            </button>
                          </>
                        )}
                        {!selectedLote && status === 'disponible' && (
                          <div className="lmi-tt-hint">
                            <span className="icon-font">touch_app</span>
                            Click para solicitar info
                          </div>
                        )}
                        {!selectedLote && status === 'apartado' && (
                          <div className="lmi-tt-hint">
                            <span className="icon-font">touch_app</span>
                            Click para ver opciones
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* LEGEND + FILTER + COUNTER overlay (REMOVED — now in top toolbar) */}

            {/* FLOATING URGENCY CTA — bottom-right corner of the plano */}
            {!loading && counts.total > 0 && counts.disponible > 0 && (
              <div className="lmi-floating-cta" data-testid="lmi-floating-cta">
                <div className="lmi-floating-cta-dot" aria-hidden="true" />
                <div className="lmi-floating-cta-text">
                  <strong>Solo quedan {counts.disponible} lotes,</strong>
                  <span> aparta el tuyo.</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* LEAD CAPTURE MODAL — captures lead and shows confirmation (no WhatsApp redirect) */}
      <LeadCaptureModal
        open={!!leadModal}
        onClose={() => setLeadModal(null)}
        title={leadModal?.mode === 'similar' ? '¿Te buscamos un lote similar?' : `Solicitar info · Lote ${leadModal?.lote?.numero}`}
        subtitle={leadModal?.mode === 'similar'
          ? `El Lote ${leadModal?.lote?.numero} Mz ${toRoman(leadModal?.lote?.manzana || 0)} ya está apartado. Déjanos tus datos y te enviamos opciones similares.`
          : `Manzana ${toRoman(leadModal?.lote?.manzana || 0)} · Modelo ${leadModal?.lote?.modelo || ''}`}
        submitLabel={leadModal?.mode === 'similar' ? 'Recibir opciones similares' : 'Solicitar información'}
        successMessage={leadModal?.mode === 'similar'
          ? `¡Listo! Recibimos tus datos. Un asesor te contacta con lotes similares disponibles en menos de 15 min en horario L-V 9-19h.`
          : `¡Listo! Recibimos tu solicitud del Lote ${leadModal?.lote?.numero}, Manzana ${toRoman(leadModal?.lote?.manzana || 0)}. Te enviamos un correo de confirmación y un asesor te contacta en menos de 15 min en horario L-V 9-19h.`}
        onSubmit={async ({ name, email, phone }) => {
          const lote = leadModal.lote;
          await postLead('lot', {
            name, email, phone,
            ...splitName(name),
            lote: String(lote.numero),
            manzana: toRoman(lote.manzana),
            modelo: lote.modelo,
            terreno_m2: Number(lote.terreno) || 0,
            construccion_m2: Number(lote.construccion) || 0,
            source_page: '/#disponibilidad',
          });
          trackEvent('lot_info_requested', {
            lote: String(lote.numero),
            manzana: toRoman(lote.manzana),
            modelo: lote.modelo,
            mode: leadModal?.mode || 'reservar',
          });
          return true;
        }}
        onSubmitted={() => {
          // Just close the underlying tooltip after lead capture.
          // The modal stays open showing the success message until user clicks "Entendido".
          setSelected(null);
        }}
      />
    </section>
  );
}
